import * as yup from 'yup'

import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { Formik } from 'formik'
import {
  ParkingEventDetailsDTO,
  UpdatePriceDTO,
} from '../../../apis/parkingEvents/dto'
import testidKeys from '../../../app/testidKeys'
import { MoovyForm } from '../../../components'
import {
  centsToEur,
  eurToCents,
  formatPrettyCurrency,
} from '../../../components/prettyCurrency'

export type OnSubmitCallback = (values: UpdatePriceDTO) => void

export type FormWhitelistedVehicleProps = {
  onSubmit: OnSubmitCallback
  formReference: string
  parkingDetails: ParkingEventDetailsDTO
  // Boolean to control whether the fee can be changed to only zero (invalidationOnly == true) or any value (invalidationOnly == false)
  invalidationOnly: boolean
}

const FormWhitelistedVehicle = (props: FormWhitelistedVehicleProps) => {
  const intl = useIntl()
  const { parkingDetails, invalidationOnly } = props

  const [formikValidateOnChange, setFormikValidateOnChange] = useState(false)
  const [formattedPrice, setFormattedPrice] = useState<string>('')

  const updateFormattedPrice = (priceStringInEur: string) => {
    setFormattedPrice(
      formatPrettyCurrency({
        intl,
        cents: eurToCents(Number(priceStringInEur)),
      }) ?? '',
    )
  }

  const defaultValues = {
    priceStringInEur: invalidationOnly
      ? '0'
      : centsToEur(parkingDetails.price ?? 0).toFixed(2),
    comment: '',
  }

  useEffect(() => {
    updateFormattedPrice(defaultValues.priceStringInEur)
  }, [])

  const validationSchema = () => {
    return yup.object().shape({
      priceStringInEur: yup
        .string()
        .test(
          'numerical',
          intl.formatMessage({ id: 'common.form.errors.number' }),
          (value) => /^[0-9.-]+$/.test(value ?? ''),
        )
        .test(
          'non-negative',
          intl.formatMessage({ id: 'common.form.errors.negative' }),
          (value) => Number(value) >= 0,
        )
        .test(
          'whole-cents-only',
          intl.formatMessage({ id: 'common.form.errors.centsOnly' }),
          (value) => Number(value) === Number(Number(value).toFixed(2)),
        )
        .required(intl.formatMessage({ id: 'common.form.errors.empty' })),
      comment: yup
        .string()
        .trim()
        .required(intl.formatMessage({ id: 'common.form.errors.empty' })),
    })
  }

  return (
    <>
      <Formik
        validate={() => setFormikValidateOnChange(true)}
        validationSchema={validationSchema()}
        enableReinitialize={true}
        initialValues={defaultValues}
        validateOnChange={formikValidateOnChange}
        onSubmit={(values) => {
          props.onSubmit({
            // `price` attribute is in cents
            price: eurToCents(Number(values.priceStringInEur)),
            comment: values.comment.trim(),
          })
        }}
      >
        {({ values, errors, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit} id={props.formReference}>
            <MoovyForm.FormContainer>
              <MoovyForm.FormBoxRow flexDirection={'column'}>
                <MoovyForm.FormTextField
                  name="priceStringInEur"
                  data-testid={testidKeys.SUMMARY_EDIT_FEE_FORM_PRICE_INPUT}
                  value={values.priceStringInEur}
                  label={intl.formatMessage({
                    id: 'page.parkingDetails.formEditFee.label.price',
                  })}
                  onChange={(event) => {
                    // Clean user input and update the form value
                    event.target.value = event.target.value
                      // Replace commas with dots
                      .replace(/,/g, '.')
                      // Only allow characters used in number values
                      .replace(/[^0-9.-]/g, '')
                    handleChange(event)
                    updateFormattedPrice(event.target.value)
                  }}
                  error={!!errors.priceStringInEur}
                  errorText={errors.priceStringInEur}
                  sx={{ display: invalidationOnly ? 'none' : 'block' }}
                  disabled={invalidationOnly}
                />
                <MoovyForm.FormTextField
                  value={formattedPrice}
                  label={intl.formatMessage({
                    id: 'page.parkingDetails.formEditFee.label.pricePreview',
                  })}
                  disabled={true}
                />
              </MoovyForm.FormBoxRow>
              <MoovyForm.FormBoxRow>
                <MoovyForm.FormTextField
                  name="comment"
                  data-testid="comment"
                  value={values.comment}
                  label={intl.formatMessage({
                    id: 'page.parkingDetails.formEditFee.label.comment',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'page.parkingDetails.formEditFee.label.comment',
                  })}
                  onChange={handleChange}
                  error={!!errors.comment}
                  errorText={errors.comment}
                />
              </MoovyForm.FormBoxRow>
            </MoovyForm.FormContainer>
          </form>
        )}
      </Formik>
    </>
  )
}

export default FormWhitelistedVehicle
